import { type TypographyProps, useTheme } from "@mui/material";
import { type FC, useRef } from "react";
import { ExpandToggleButton } from "web_myplann/Shared/Components/ExpandToggleButton";
import { MultiLineText } from "web_myplann/Shared/Components/MultiLineText";
import { MyPlannHtmlContent } from "web_myplann/Shared/Components/MyPlannHtmlContent";
import { ShowAllToggleButton } from "web_myplann/Shared/Components/ShowAllToggleButton";
import { useTextTruncation } from "web_myplann/Shared/Hooks/useTextTruncation";
import type { ExpandableTextToggle } from "web_myplann/Shared/Types/ExpandableTextToggle";
import { getHtmlContentSx } from "web_myplann/Shared/Utils/htmlContentUtils/getHtmlContentSx";
import { isHtmlContent } from "web_myplann/Shared/Utils/htmlContentUtils/isHtmlContent";

type Props = {
	text: string;
	lines?: number;
	variant?: TypographyProps["variant"];
	color?: string;
	toggle?: ExpandableTextToggle;
	clampHtml?: boolean;
};

const DEFAULT_PREVIEW_LINES = 3;

export const ExpandableText: FC<Props> = ({
	text,
	lines = DEFAULT_PREVIEW_LINES,
	variant = "body2",
	color,
	toggle = "showAll",
	clampHtml = true,
}) => {
	const theme = useTheme();
	const textRef = useRef<HTMLDivElement>(null);
	const { isTruncated, isExpanded, toggleExpanded } = useTextTruncation(
		textRef,
		[text, lines],
	);

	const isHtml = isHtmlContent(text);
	const showToggle = isTruncated || isExpanded;

	return (
		<>
			{isHtml && (
				<MyPlannHtmlContent
					ref={textRef}
					html={text}
					sx={getHtmlContentSx({
						theme,
						showMore: clampHtml && !isExpanded,
						maxLines: lines,
					})}
				/>
			)}
			{!isHtml && (
				<MultiLineText
					ref={textRef}
					variant={variant}
					color={color}
					clamp={isExpanded ? undefined : lines}
				>
					{text}
				</MultiLineText>
			)}
			{showToggle && toggle === "showMore" && (
				<ExpandToggleButton isExpanded={isExpanded} onClick={toggleExpanded} />
			)}
			{showToggle && toggle === "showAll" && (
				<ShowAllToggleButton
					variant="textLink"
					expanded={isExpanded}
					onClick={toggleExpanded}
					sx={{ alignSelf: "flex-start" }}
				/>
			)}
		</>
	);
};
